import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useFloatingPanel } from "@/lib/useFloatingPanel";

/**
 * Eigener Datepicker (deutsch, Wochenstart Montag) mit optionalem Mindestdatum –
 * z. B. für Wiedervorlagen, bei denen nur zukünftige Termine sinnvoll sind.
 *
 * - `DatePickerCalendar`: eingebetteter Kalender (für Popovers/Dialoge).
 * - `DatePickerField`: Button mit Datumsanzeige, öffnet den Kalender als Popover.
 *
 * Alle Werte sind ISO-Datumsstrings (YYYY-MM-DD, lokale Zeit – keine UTC-Sprünge).
 */

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
const MONTH_LABELS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
] as const;

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function isoToday(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth(), now.getDate());
}

function isoAddDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y!, (m ?? 1) - 1, d ?? 1);
  date.setDate(date.getDate() + days);
  return toIso(date.getFullYear(), date.getMonth(), date.getDate());
}

function normalizeIso(value?: string | null): string | null {
  const raw = String(value || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

export function formatIsoDate(value?: string | null): string {
  const iso = normalizeIso(value);
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

interface QuickOption {
  label: string;
  iso: string;
}

export function DatePickerCalendar({
  value,
  onSelect,
  minDate,
  showQuickOptions = true,
  className,
}: {
  /** Ausgewähltes Datum (YYYY-MM-DD) oder null. */
  value?: string | null;
  onSelect: (iso: string) => void;
  /** Frühestes wählbares Datum (YYYY-MM-DD), z. B. heute für Wiedervorlagen. */
  minDate?: string | null;
  /** Schnellauswahl (Heute / Morgen / In 1 Woche) anzeigen. */
  showQuickOptions?: boolean;
  className?: string;
}) {
  const today = isoToday();
  const selected = normalizeIso(value);
  const min = normalizeIso(minDate);

  const initialIso = selected ?? (min && min > today ? min : today);
  const [viewYear, setViewYear] = useState(() => Number(initialIso.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(() => Number(initialIso.slice(5, 7)) - 1);

  // Bei externem Wertwechsel (z. B. Schnellauswahl) zum Monat springen.
  useEffect(() => {
    if (!selected) return;
    setViewYear(Number(selected.slice(0, 4)));
    setViewMonth(Number(selected.slice(5, 7)) - 1);
  }, [selected]);

  const cells = useMemo(() => {
    const firstWeekday = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Mo=0
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const out: Array<{ day: number; iso: string } | null> = [];
    for (let i = 0; i < firstWeekday; i += 1) out.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      out.push({ day, iso: toIso(viewYear, viewMonth, day) });
    }
    return out;
  }, [viewYear, viewMonth]);

  const prevDisabled = useMemo(() => {
    if (!min) return false;
    const minYear = Number(min.slice(0, 4));
    const minMonth = Number(min.slice(5, 7)) - 1;
    return viewYear < minYear || (viewYear === minYear && viewMonth <= minMonth);
  }, [min, viewYear, viewMonth]);

  const quickOptions = useMemo<QuickOption[]>(() => {
    if (!showQuickOptions) return [];
    return [
      { label: "Heute", iso: today },
      { label: "Morgen", iso: isoAddDays(today, 1) },
      { label: "In 1 Woche", iso: isoAddDays(today, 7) },
    ].filter((opt) => !min || opt.iso >= min);
  }, [showQuickOptions, today, min]);

  const shiftMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  return (
    <div className={clsx("w-64 select-none", className)}>
      {quickOptions.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1">
          {quickOptions.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => onSelect(opt.iso)}
              className={clsx(
                "rounded-full px-2.5 py-1 text-xs font-medium transition",
                selected === opt.iso
                  ? "bg-accent text-label-on-accent"
                  : "bg-fill text-label-secondary hover:bg-fill-tertiary hover:text-label",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          disabled={prevDisabled}
          aria-label="Vorheriger Monat"
          className="flex h-7 w-7 items-center justify-center rounded-full text-label-secondary hover:bg-fill-tertiary disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
        </button>
        <p className="text-sm font-semibold text-label">
          {MONTH_LABELS[viewMonth]} {viewYear}
        </p>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          aria-label="Nächster Monat"
          className="flex h-7 w-7 items-center justify-center rounded-full text-label-secondary hover:bg-fill-tertiary"
        >
          <ChevronRightIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-0.5 text-center">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="py-1 text-[11px] font-medium text-label-tertiary">
            {label}
          </span>
        ))}
        {cells.map((cell, idx) => {
          if (!cell) return <span key={`blank-${idx}`} />;
          const disabled = !!min && cell.iso < min;
          const isSelected = selected === cell.iso;
          const isToday = cell.iso === today;
          return (
            <button
              key={cell.iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(cell.iso)}
              aria-label={formatIsoDate(cell.iso)}
              aria-pressed={isSelected}
              className={clsx(
                "mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition",
                isSelected
                  ? "bg-accent font-semibold text-label-on-accent"
                  : disabled
                    ? "cursor-not-allowed text-label-tertiary opacity-40"
                    : "text-label hover:bg-fill-tertiary",
                isToday && !isSelected && "font-semibold text-accent ring-1 ring-inset ring-accent/60",
              )}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Kalenderbreite inkl. Padding – unabhängig von der Triggerbreite. */
const FIELD_PANEL_WIDTH = 280;
/** Volle Kalenderhöhe (Schnellauswahl + Kopf + 6 Wochen), für Flip-Entscheidung. */
const FIELD_PANEL_HEIGHT = 340;
const VIEWPORT_PAD = 8;

export function DatePickerField({
  value,
  onChange,
  minDate,
  allowClear = false,
  placeholder = "Datum wählen",
  align = "left",
  buttonClassName,
}: {
  value?: string | null;
  onChange: (iso: string | null) => void;
  minDate?: string | null;
  allowClear?: boolean;
  placeholder?: string;
  /** Ausrichtung des Popovers relativ zum Button. */
  align?: "left" | "right";
  buttonClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const selected = normalizeIso(value);
  const floating = useFloatingPanel(buttonRef, open, {
    gap: 4,
    maxHeightCap: 380,
    minBelow: FIELD_PANEL_HEIGHT,
    contentHeightEstimate: FIELD_PANEL_HEIGHT,
  });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const panelStyle = useMemo(() => {
    if (!floating) return undefined;
    let left =
      align === "right"
        ? floating.left + floating.width - FIELD_PANEL_WIDTH
        : floating.left;
    const maxLeft = Math.max(VIEWPORT_PAD, window.innerWidth - FIELD_PANEL_WIDTH - VIEWPORT_PAD);
    left = Math.min(Math.max(VIEWPORT_PAD, left), maxLeft);
    return {
      position: "fixed" as const,
      top: floating.top,
      left,
      maxHeight: floating.maxHeight,
      ...(floating.transform ? { transform: floating.transform } : {}),
    };
  }, [floating, align]);

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "inline-flex h-9 items-center gap-2 rounded-lg border border-separator bg-card px-3 text-sm transition hover:bg-fill-tertiary",
          selected ? "text-label" : "text-label-tertiary",
          buttonClassName,
        )}
      >
        <CalendarDaysIcon className="h-4 w-4 shrink-0 text-label-secondary" aria-hidden />
        <span>{selected ? formatIsoDate(selected) : placeholder}</span>
        {allowClear && selected ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Datum entfernen"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onChange(null);
              }
            }}
            className="ml-1 rounded-full p-0.5 text-label-tertiary hover:bg-fill hover:text-label"
          >
            <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
          </span>
        ) : null}
      </button>
      {open && floating && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={panelRef}
              className="z-[900] overflow-y-auto rounded-ios border border-separator bg-card p-3 shadow-ios-md"
              style={panelStyle}
              onMouseDown={(e) => e.preventDefault()}
            >
              <DatePickerCalendar
                value={selected}
                minDate={minDate}
                onSelect={(iso) => {
                  onChange(iso);
                  setOpen(false);
                }}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
